import {
  AppCard,
  CopyButton,
  Icon,
  PageContainer,
  RouteIconButtonLink,
  RouteLink,
  RouteTabLink,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiClose, mdiEye } from "@mdi/js";
import { TabContext } from "@mui/lab";
import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { EntitiesQuotationCollectionMain } from "../../../../__generated__/orval/models/entitiesQuotationCollectionMain.zod";
import { ForbiddenError } from "../../../../errors/ForbiddenError";
import { useAuth } from "../../../../hooks/useAuth";
import { i18nextParseToDate } from "../../../../utils/i18nextParseTo";
import { CreateQuotationBatchButton } from "./-internal_components/CreateQuotationBatchButton";
import { MassAssignThreatButton } from "./-internal_components/MassAssignThreatButton";
import { MassAssignToButton } from "./-internal_components/MassAssignToButton";
import { MassReopenOfferWindowButton } from "./-internal_components/MassReopenOfferWindowButton";
import { QuotationsFilters } from "./-internal_components/QuotationsFilters";
import { quotationsListQueryOptions } from "./-queries";

const textComparisonSchema = z.object({
  cont: z.optional(z.string()),
  end: z.optional(z.string()),
  eq: z.optional(z.string()),
  ne: z.optional(z.string()),
  start: z.optional(z.string()),
});

const searchSchema = z.object({
  assignedTo: z.optional(z.array(z.coerce.number())).catch(undefined),
  editQuotationBatchId: z.optional(z.coerce.number()).catch(undefined),
  identifier: z.optional(textComparisonSchema).catch(undefined),
  materialGroup: z.optional(z.array(z.coerce.number())).catch(undefined),
  name: z.optional(textComparisonSchema).catch(undefined),
  offersPresence: z
    .optional(z.enum(["all", "with_offers", "without_offers"]))
    .catch(undefined),
  order: z
    .optional(
      z.object({
        assignedTo: z.optional(z.enum(["asc", "desc"])).catch(undefined),
        identifier: z.optional(z.enum(["asc", "desc"])).catch(undefined),
        materialGroup: z.optional(z.enum(["asc", "desc"])).catch(undefined),
        name: z.optional(z.enum(["asc", "desc"])).catch(undefined),
        plant: z.optional(z.enum(["asc", "desc"])).catch(undefined),
        purchaseGroup: z.optional(z.enum(["asc", "desc"])).catch(undefined),
        quantity: z.optional(z.enum(["asc", "desc"])).catch(undefined),
        segment: z.optional(z.enum(["asc", "desc"])).catch(undefined),
        status: z.optional(z.enum(["asc", "desc"])).catch(undefined),
        type: z.optional(z.enum(["asc", "desc"])).catch(undefined),
        urgency: z.optional(z.enum(["asc", "desc"])).catch(undefined),
      }),
    )
    .default({ identifier: "desc" })
    .catch({ identifier: "desc" }),
  orderNumber: z.optional(textComparisonSchema).catch(undefined),
  page: z.optional(z.coerce.number()).catch(undefined),
  plantId: z.optional(z.array(z.coerce.number())).catch(undefined),
  purchaseGroup: z.optional(z.array(z.coerce.number())).catch(undefined),
  referenceNumber: z.optional(z.string()).catch(undefined),
  renegotiation: z.optional(z.boolean()).catch(undefined),
  requisitionDate: z.optional(z.string()).catch(undefined),
  revisionCode: z.optional(textComparisonSchema).catch(undefined),
  rowsPerPage: z.optional(z.coerce.number()).catch(undefined),
  segment: z.optional(z.array(z.coerce.number())).catch(undefined),
  status: z.optional(z.array(z.coerce.string())).catch(undefined),
  tab: z
    .enum([
      "all",
      "open",
      "ongoing",
      "pending_buyer",
      "follow_up_deliveries",
      "completed",
    ])
    .default("all")
    .catch("all"),
  type: z.optional(z.enum(["assisted", "automatic"])).catch(undefined),
  urgency: z
    .optional(z.enum(["threat", "urgent", "not_urgent"]))
    .catch(undefined),
});

type OrderSearchSchema = z.infer<typeof searchSchema.shape.order>;
type TabType = z.infer<typeof searchSchema.shape.tab>;

const QuotationTableRow: React.FC<{
  quotation: EntitiesQuotationCollectionMain;
  isSelected: boolean;
  onSelect: (id: number) => void;
  showCheckbox: boolean;
  tab: TabType;
}> = ({ quotation, isSelected, onSelect, showCheckbox, tab }) => {
  const { t } = useTranslation();

  const getUrgencyTranslationKey = (urgency: string): string => {
    const urgencyMap: Record<string, string> = {
      not_urgent: "common.notUrgent",
      threat: "common.threat",
      urgent: "common.urgent",
    };
    return urgencyMap[urgency] || "";
  };

  const getUrgencyColor = (
    urgency: string,
  ): "error" | "warning" | "info" | "default" => {
    switch (urgency) {
      case "threat":
        return "error";
      case "urgent":
        return "warning";
      case "not_urgent":
        return "info";
      default:
        return "default";
    }
  };

  return (
    <TableRow>
      {!!showCheckbox && (
        <TableCell align="center" padding="checkbox">
          <Checkbox
            checked={isSelected}
            onChange={() => onSelect(quotation.id)}
          />
        </TableCell>
      )}
      <TableCell align="center">
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            gap: 1,
          }}
        >
          <CopyButton textToCopy={quotation.identifier} />
          <RouteLink
            params={{ quotationId: String(quotation.id) }}
            target="_blank"
            to="/vbuyer/quotations/$quotationId"
          >
            #{quotation.identifier}
          </RouteLink>
        </Box>
      </TableCell>
      <TableCell align="center">
        {quotation.material.short_description}
      </TableCell>
      <TableCell align="center">{quotation.segment?.name}</TableCell>
      <TableCell align="center">{quotation.material_group?.name}</TableCell>
      <TableCell align="center">
        <Tooltip title={quotation.plant.name}>
          <Typography variant="body2">
            {quotation.plant.plant_number}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell align="center">
        <Chip
          color={getUrgencyColor(quotation.urgency)}
          label={t(getUrgencyTranslationKey(quotation.urgency))}
          size="small"
          variant="filled"
        />
      </TableCell>
      <TableCell align="center">
        {quotation.requisition_date
          ? t("intlDate", i18nextParseToDate(quotation.requisition_date))
          : "-"}
      </TableCell>
      {["all", "ongoing"].includes(tab) && (
        <TableCell align="center">
          <Chip
            color={quotation.renegotiation === true ? "warning" : "default"}
            label={
              quotation.renegotiation === true
                ? t("common.yes")
                : t("common.no")
            }
            size="small"
            variant="outlined"
          />
        </TableCell>
      )}
      <TableCell align="center">{quotation.status}</TableCell>
      <TableCell align="center">
        {!!quotation.assigned_to && (
          <Tooltip title={quotation.assigned_to.login}>
            <Typography variant="body2">
              {quotation.assigned_to.full_name}
            </Typography>
          </Tooltip>
        )}
      </TableCell>
      <TableCell align="center">
        <Tooltip
          placement="top"
          sx={{ fontSize: "8em" }}
          title={quotation.purchase_group?.description || ""}
        >
          <span>{quotation.purchase_group?.name}</span>
        </Tooltip>
      </TableCell>
      <TableCell align="center">
        <RouteIconButtonLink
          params={{ quotationId: quotation.id.toString() }}
          target="_blank"
          to="/vbuyer/quotations/$quotationId/details"
        >
          <Icon icon={mdiEye} />
        </RouteIconButtonLink>
      </TableCell>
    </TableRow>
  );
};

const QuotationsTable: React.FC = () => {
  const { user } = useAuth();
  const navigate = Route.useNavigate();
  const {
    page = 1,
    rowsPerPage = 25,
    tab,
    editQuotationBatchId,
    order = {},
  } = Route.useSearch();
  const loaderDeps = Route.useLoaderDeps();

  const { t } = useTranslation();

  const { data: quotationsResponse, isLoading } = useSuspenseQuery(
    quotationsListQueryOptions(loaderDeps),
  );

  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [lastTab, setLastTab] = useState(tab);

  const showCheckboxes = useMemo(() => true, []);

  if (lastTab !== tab) {
    setLastTab(tab);
    if (selectedItems.length > 0) {
      setSelectedItems([]);
    }
  }

  const handleChangePage = useCallback(
    (
      _event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
      newPage: number,
    ) => {
      navigate({
        search: (prev) => ({
          ...prev,
          page: newPage + 1,
        }),
      });
    },
    [navigate],
  );

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      navigate({
        search: (prev) => ({
          ...prev,
          page: 1,
          rowsPerPage: parseInt(event.target.value, 10),
        }),
      });
    },
    [navigate],
  );

  const handleSort = useCallback(
    (column: keyof OrderSearchSchema) => {
      const isAsc = order[column] === "asc";
      const isDesc = order[column] === "desc";

      if (isDesc) {
        // If currently descending, remove the sort
        navigate({
          search: (prev) => {
            const newOrder = { ...prev.order };
            delete newOrder[column];
            return {
              ...prev,
              order: newOrder,
              page: 1,
            };
          },
        });
        return;
      }

      // If currently ascending or not sorted, set to ascending or descending
      navigate({
        search: (prev) => ({
          ...prev,
          order: {
            ...prev.order,
            [column]: isAsc ? "desc" : "asc",
          },
          page: 1,
        }),
      });
    },
    [navigate, order],
  );

  const quotations = quotationsResponse.results;

  const isSelected = useCallback(
    (id: number) => selectedItems.includes(id),
    [selectedItems],
  );

  const isAllSelected = useMemo(
    () => quotations.length > 0 && selectedItems.length === quotations.length,
    [quotations.length, selectedItems.length],
  );

  const handleSelectItem = useCallback(
    (id: number) => {
      if (isSelected(id)) {
        setSelectedItems(selectedItems.filter((itemId) => itemId !== id));
      } else {
        setSelectedItems([...selectedItems, id]);
      }
    },
    [isSelected, selectedItems],
  );

  const handleSelectAllClick = useCallback(() => {
    if (isAllSelected) {
      setSelectedItems([]);
    } else {
      setSelectedItems(quotations.map((item) => item.id));
    }
  }, [quotations, isAllSelected]);

  const handleActionComplete = useCallback(() => {
    setSelectedItems([]);
  }, []);

  const getColSpan = (): number => {
    const baseColumns = tab === "open" ? 14 : 16;
    return showCheckboxes ? baseColumns + 1 : baseColumns;
  };

  return (
    <Box>
      {selectedItems.length > 0 && showCheckboxes && (
        <Box
          sx={{
            bgcolor: "tokens.mutedSurface",
            borderRadius: 1,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            marginY: 0,
            p: 1,
          }}
        >
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              gap: 2,
              justifyContent: "space-between",
            }}
          >
            <IconButton onClick={() => setSelectedItems([])}>
              <Icon icon={mdiClose} />
            </IconButton>
            <Typography sx={{ flexGrow: 1 }} variant="subtitle1">
              {t("common.selectedItems", {
                count: selectedItems.length,
              })}
            </Typography>

            {tab === "open" &&
              user?.roles_names.includes("Quotation Batch - Create") && (
                <CreateQuotationBatchButton
                  editQuotationBatchId={editQuotationBatchId}
                  quotationIds={selectedItems}
                />
              )}

            <MassAssignThreatButton
              onActionComplete={handleActionComplete}
              quotationIds={selectedItems}
            />

            <MassAssignToButton
              onActionComplete={handleActionComplete}
              quotationIds={selectedItems}
            />

            {tab === "pending_buyer" && (
              <MassReopenOfferWindowButton
                onActionComplete={handleActionComplete}
                quotationIds={selectedItems}
              />
            )}
          </Box>
        </Box>
      )}
      <TableContainer>
        <Table padding="checkbox">
          <TableHead>
            <TableRow>
              {showCheckboxes && (
                <TableCell align="center" padding="checkbox">
                  <Tooltip
                    title={
                      isAllSelected
                        ? t("common.uncheckAll")
                        : t("common.checkAll")
                    }
                  >
                    <Checkbox
                      checked={isAllSelected}
                      indeterminate={
                        selectedItems.length > 0 &&
                        selectedItems.length < quotations.length
                      }
                      onChange={handleSelectAllClick}
                    />
                  </Tooltip>
                </TableCell>
              )}
              <TableCell align="center">
                <TableSortLabel
                  active={!!order.identifier}
                  direction={order.identifier === "asc" ? "asc" : "desc"}
                  onClick={() => handleSort("identifier")}
                >
                  {t("quotation.requestNumber")}
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={!!order.name}
                  direction={order.name === "asc" ? "asc" : "desc"}
                  onClick={() => handleSort("name")}
                >
                  {t("common.name")}
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={!!order.segment}
                  direction={order.segment === "asc" ? "asc" : "desc"}
                  onClick={() => handleSort("segment")}
                >
                  {t("common.segment")}
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={!!order.materialGroup}
                  direction={order.materialGroup === "asc" ? "asc" : "desc"}
                  onClick={() => handleSort("materialGroup")}
                >
                  {t("common.materialGroup")}
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={!!order.plant}
                  direction={order.plant === "asc" ? "asc" : "desc"}
                  onClick={() => handleSort("plant")}
                >
                  {t("common.plant")}
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={!!order.urgency}
                  direction={order.urgency === "asc" ? "asc" : "desc"}
                  onClick={() => handleSort("urgency")}
                >
                  {t("quotation.urgency")}
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                {t("common.requisitionDate")}
              </TableCell>
              {["all", "ongoing"].includes(tab) && (
                <TableCell align="center">
                  {t("quotation.renegotiation")}
                </TableCell>
              )}
              <TableCell align="center">
                <TableSortLabel
                  active={!!order.status}
                  direction={order.status === "asc" ? "asc" : "desc"}
                  onClick={() => handleSort("status")}
                >
                  {t("common.status")}
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={!!order.assignedTo}
                  direction={order.assignedTo === "asc" ? "asc" : "desc"}
                  onClick={() => handleSort("assignedTo")}
                >
                  {t("quotation.assignedTo")}
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={!!order.purchaseGroup}
                  direction={order.purchaseGroup === "asc" ? "asc" : "desc"}
                  onClick={() => handleSort("purchaseGroup")}
                >
                  {t("common.purchaseGroup")}
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">{t("common.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!!isLoading && (
              <TableRow key={crypto.randomUUID()}>
                <TableCell align="center" colSpan={getColSpan()}>
                  <Skeleton />
                </TableCell>
              </TableRow>
            )}
            {!isLoading && quotations.length === 0 && (
              <TableRow>
                <TableCell align="center" colSpan={getColSpan()}>
                  <Typography
                    sx={{
                      color: "text.secondary",
                      py: 4,
                    }}
                    variant="body2"
                  >
                    {t("common.noResultsFound")}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!isLoading &&
              quotations.length > 0 &&
              quotations.map((quotation) => (
                <QuotationTableRow
                  isSelected={isSelected(quotation.id)}
                  key={quotation.id}
                  onSelect={handleSelectItem}
                  quotation={quotation}
                  showCheckbox={showCheckboxes}
                  tab={tab}
                />
              ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                count={quotationsResponse.total_items}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                page={page - 1}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                showFirstButton
                showLastButton
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Box>
  );
};

const RouteComponent: React.FC = () => {
  const navigate = Route.useNavigate();
  const { tab } = Route.useSearch();

  const { t } = useTranslation();

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: TabType) => {
      navigate({
        search: (prev) => ({
          ...prev,
          page: 1,
          tab: newValue,
        }),
      });
    },
    [navigate],
  );

  return (
    <PageContainer
      maxWidth={false}
      showBackButton={false}
      title={t("quotation.quotations")}
    >
      <AppCard>
        <QuotationsFilters />

        <TabContext value={tab}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs onChange={handleTabChange} value={tab}>
              <RouteTabLink
                from="/vbuyer/quotations/"
                label={t("quotation.tabs.all")}
                search={(prev) => ({ ...prev, page: 1, tab: "all" })}
                to="/vbuyer/quotations"
                value="all"
              />
              <RouteTabLink
                from="/vbuyer/quotations/"
                label={t("quotation.tabs.open")}
                search={(prev) => ({
                  ...prev,
                  page: 1,
                  status: undefined,
                  tab: "open",
                })}
                to="/vbuyer/quotations"
                value="open"
              />
              <RouteTabLink
                from="/vbuyer/quotations/"
                label={t("quotation.tabs.ongoing")}
                search={(prev) => ({
                  ...prev,
                  page: 1,
                  status: undefined,
                  tab: "ongoing",
                })}
                to="/vbuyer/quotations"
                value="ongoing"
              />
              <RouteTabLink
                from="/vbuyer/quotations/"
                label={t("quotation.tabs.pendingBuyers")}
                search={(prev) => ({
                  ...prev,
                  page: 1,
                  status: undefined,
                  tab: "pending_buyer",
                })}
                to="/vbuyer/quotations"
                value="pending_buyer"
              />
              <RouteTabLink
                from="/vbuyer/quotations/"
                label={t("quotation.tabs.followUpDeliveries")}
                search={(prev) => ({
                  ...prev,
                  page: 1,
                  status: undefined,
                  tab: "follow_up_deliveries",
                })}
                to="/vbuyer/quotations"
                value="follow_up_deliveries"
              />
              <RouteTabLink
                from="/vbuyer/quotations/"
                label={t("quotation.tabs.completed")}
                search={(prev) => ({
                  ...prev,
                  page: 1,
                  status: undefined,
                  tab: "completed",
                })}
                to="/vbuyer/quotations"
                value="completed"
              />
            </Tabs>
          </Box>
          <Suspense
            fallback={
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <CircularProgress size={24} sx={{ margin: 2 }} />
              </Box>
            }
          >
            <QuotationsTable />
          </Suspense>
        </TabContext>
      </AppCard>
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/quotations/",
  // biome-ignore assist/source/useSortedKeys: ordering required for type inference, waiting a TanStack Biome plugin
)({
  validateSearch: searchSchema,
  component: RouteComponent,
  loaderDeps: ({ search }) => {
    let statusFilter: string[] | undefined;

    if (search.tab === "open") {
      statusFilter = ["Waiting Send"];
    } else if (search.tab === "ongoing") {
      statusFilter = [
        "Waiting Bid",
        "Waiting Offers",
        "Waiting For Approval",
        "Waiting For Renegotiation",
        "Waiting Additional Information",
        "Validating Vendor",
        "Solving Doubts",
        "Pending Technical Approval",
        "Pending Buyer Approval",
        "Issuing Purchase Order",
        "Choosing Winner",
      ];
    } else if (search.tab === "completed") {
      statusFilter = ["Canceled", "Quotation Refused", "Finished"];
    } else if (search.tab === "pending_buyer") {
      statusFilter = ["Pending Buyer"];
    } else if (search.tab === "follow_up_deliveries") {
      statusFilter = ["Waiting Delivery Date", "In Receiving"];
    } else {
      statusFilter = search.status;
    }

    return {
      filters: {
        assigned_to: search.assignedTo,
        "identifier[cont]": search.identifier?.cont,
        "identifier[end]": search.identifier?.end,
        "identifier[eq]": search.identifier?.eq,
        "identifier[ne]": search.identifier?.ne,
        "identifier[start]": search.identifier?.start,
        material_group: search.materialGroup,
        "name[cont]": search.name?.cont,
        "name[end]": search.name?.end,
        "name[eq]": search.name?.eq,
        "name[ne]": search.name?.ne,
        "name[start]": search.name?.start,
        offers_presence:
          search.offersPresence && search.offersPresence !== "all"
            ? search.offersPresence
            : undefined,
        "order_number[cont]": search.orderNumber?.cont,
        "order_number[end]": search.orderNumber?.end,
        "order_number[eq]": search.orderNumber?.eq,
        "order_number[ne]": search.orderNumber?.ne,
        "order_number[start]": search.orderNumber?.start,
        plant: search.plantId,
        purchase_group: search.purchaseGroup,
        reference_number: search.referenceNumber,
        renegotiation: search.renegotiation,
        requisition_date: search.requisitionDate,
        "revision_code[cont]": search.revisionCode?.cont,
        "revision_code[end]": search.revisionCode?.end,
        "revision_code[eq]": search.revisionCode?.eq,
        "revision_code[ne]": search.revisionCode?.ne,
        "revision_code[start]": search.revisionCode?.start,
        segment: search.segment,
        status: statusFilter,
        type: search.tab === "open" ? "assisted" : search.type,
        urgency: search.urgency,
      },
      items_per_page: search.rowsPerPage || 25,
      orders: {
        "order[assigned_to.full_name_with_login]": search.order.assignedTo,
        "order[identifier]": search.order.identifier,
        "order[material_group.name]": search.order.materialGroup,
        "order[material.short_description]": search.order.name,
        "order[plant.plant_number]": search.order.plant,
        "order[purchase_group.name]": search.order.purchaseGroup,
        "order[quantity]": search.order.quantity,
        "order[segment.name]": search.order.segment,
        "order[status]": search.order.status,
        "order[type]": search.order.type,
        "order[urgency]": search.order.urgency,
      },
      page: search.page || 1,
    };
  },
  beforeLoad({ context }) {
    if (context.auth.user?.roles_names.includes("View Quotations")) {
      return;
    }

    throw new ForbiddenError();
  },
  loader: ({ context, deps }) => {
    context.queryClient.prefetchQuery(quotationsListQueryOptions(deps));

    return null;
  },
});
